import { useEffect, useRef, useState } from "react";
import { fetchCombinedSearch } from "../services/news.service";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getFilterDateValueKeys } from "utils/date-filter.util";
import { useSourceSearch } from "features/SourceSearch";
import { useDispatch } from "react-redux";
import { setCategories } from "context/features/dataSlice";
import { biasRating } from "data/biasRatingLabel";
import { publishers } from "data/publishers";
import { searchEngineCounts, currentSearchEngine, searchEngineOffsets } from "config";

const useNewsData = (
  setGlobalLoading,
  query,
  categoriesCopy,
  setLoadingStates
) => {
  const [combinedSearch, setCombinedSearch] = useState([]);
  const dispatch = useDispatch();
  const timeoutRef = useRef();

  const { currentTimeframe, startDate, endDate, currentSource } =
    useSourceSearch();
  const {source} = useParams();
  const { categories } = useSelector((state) => state.data);

  const filteredDateValue = getFilterDateValueKeys(
    currentTimeframe,
    startDate,
    endDate
  );

  const tempCategories = categoriesCopy.length > 0 ? source === "rating" ? biasRating : source === "partner-publishers" ? publishers : categories : [];
  
  const visibleCategoryNames = tempCategories
    .filter((cat) => !cat.isHidden_ysn)
    .map((cat) => cat.categoryName_chr);

  const fetchCombinedSearchData = async () => {
    setGlobalLoading(true);
    setCombinedSearch([]);
    setLoadingStates({});

    try {
      visibleCategoryNames.forEach((cat) =>
        setLoadingStates((prev) => ({ ...prev, [cat]: true }))
      );

      if (
        source !== "rating" &&
        visibleCategoryNames[0]?.includes("Left & Lean Left")
      ) {
        return;
      }

      // first page
      const firstPage = await fetchCombinedSearch(
        query,
        filteredDateValue,
        visibleCategoryNames,
        `${searchEngineCounts[currentSearchEngine]}`,
        "0"
      );

      setCombinedSearch(firstPage.results);
      visibleCategoryNames.forEach((cat) =>
        setLoadingStates((prev) => ({ ...prev, [cat]: false }))
      );
      if (source === "partner-publishers") {
      const orderedPublishers = firstPage.results
        .map(result =>
          publishers.find(pub => pub.categoryName_chr === result.media)
        )
        .filter(Boolean);

      dispatch(setCategories(orderedPublishers));
      } else if (source !== "rating") {
        dispatch(setCategories(firstPage.categories));
      }

    } catch (e) {
      console.error("Error fetching data:", e);
      visibleCategoryNames.forEach((cat) =>
        setLoadingStates((prev) => ({ ...prev, [cat]: false }))
      );
    } finally {
      setGlobalLoading(false);
    }
  };
  
  const fetchMore = async (category) => {
    if (searchEngineCounts[currentSearchEngine] > 20) return;
    try {
      const nextPage = await fetchCombinedSearch(
        query,
        filteredDateValue,
        [category],
        `${20 - searchEngineCounts[currentSearchEngine]}`,
        `${searchEngineOffsets[currentSearchEngine]}`
      );
      setCombinedSearch((prev) => {
        const merged = [...prev];
        const newSection = nextPage.results[0]; // Since we only fetch one category
        const i = merged.findIndex((s) => s.media === newSection.media);
        
        if (i > -1) {
          merged[i] = {
            ...merged[i],
            webPages: {
              ...merged[i].webPages,
              value: [
                ...(merged[i].webPages?.value ?? []),
                ...(newSection.webPages?.value ?? []),
              ],
            },
          };
        } else {
          merged.push(newSection);
        }
        return merged;
      });
    } catch (error) {
      console.error('Error fetching more results:', error);
    } finally {
    }
  };
  useEffect(() => {
    if (query && categoriesCopy.length > 0) fetchCombinedSearchData();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query, filteredDateValue, categoriesCopy, source]);

  return { combinedSearch, fetchMore };
};

export default useNewsData;
